/**
 * bd-view-tracking.js
 *
 * Reports what a visitor is browsing to GA4 G-9HLZQW6SRQ (first-party via
 * window.bdGa4Browse from bd-ga4-g9-browse.js) so the category behaviour
 * behind the on-site personalization is also measurable in GA4. The GTM
 * container that used to consume the dataLayer pushes (GTM-TWC9KZRX) was
 * removed 2026-08-31 — it fired another brand's IDs; see
 * ops/gtm-wrong-ads-id-2026-08/. The dataLayer pushes stay as the wrap
 * backup path.
 *
 * Two kinds of events are pushed to window.dataLayer:
 *
 * 1. bd_* custom events — always sent (helper first, dataLayer backup).
 *    Nothing else on the storefront emits
 *    these, so there is no double-counting risk. They carry the store's own
 *    taxonomy (cat-* / subcat-* / material-* / look-*), which Shopify's own
 *    analytics does not know about:
 *      bd_product_view   bd_departments, bd_subcategories, bd_materials, …
 *      bd_category_view  bd_collection, bd_departments
 *      bd_affinity       bd_affinity_department, bd_affinity_focus, …
 *
 * 2. GA4 standard ecommerce events (view_item, view_item_list, search,
 *    add_to_cart, begin_checkout) — only when Theme settings →
 *    Personalization → "Send GA4 ecommerce events from the theme" is on. Leave it OFF while Shopify's own GA4
 *    channel (Customer events / web pixels) is sending them, or both will be
 *    counted. Since the 2026-08-21 cutover disconnected the Google & YouTube
 *    channel from GA4, this theme is the only browse-event sender (see
 *    ops/ga4-add-to-cart-restore-2026-08/), so the setting must stay ON.
 *
 * After each GA4 payload is built, also call window.bdGa4Browse(name, params)
 * (or queue on window.bdGa4BrowseQueue if the helper is not defined yet).
 * That is the G-9 contract — do not rely on the dataLayer wrap in
 * bd-ga4-g9-browse.js. Pass the ecommerce object / search_term params, never
 * the {ecommerce:null} clear. Do not send purchase from this file.
 *
 * Params are scalars or pipe-joined strings because GA4 event parameters
 * cannot hold arrays. See docs/personalization.md for the GA4 setup.
 */
(function () {
  'use strict';

  var ctx = window.BDPageContext || {};
  if (window.Shopify && window.Shopify.designMode) return;

  window.dataLayer = window.dataLayer || [];

  function push(payload) {
    window.dataLayer.push(payload);
  }

  /**
   * First-party G-9 path. Helper is the contract; dataLayer wrap is backup.
   * Queue if bd-ga4-g9-browse.js has not defined the helper yet (script order
   * currently loads browse first; the queue means PDP view_item cannot drop
   * if that order changes).
   */
  function sendG9(name, params) {
    if (typeof window.bdGa4Browse === 'function') window.bdGa4Browse(name, params);
    else {
      window.bdGa4BrowseQueue = window.bdGa4BrowseQueue || [];
      window.bdGa4BrowseQueue.push([name, params]);
    }
  }

  /**
   * bd_* taxonomy events: same helper-first contract. Copies the payload
   * minus the event key (skipping null/undefined, matching the wrap's
   * copyParams so the helper's fingerprint claim dedupes the backup push).
   */
  function emitBd(payload) {
    var params = {};
    for (var key in payload) {
      if (!Object.prototype.hasOwnProperty.call(payload, key)) continue;
      if (key === 'event' || payload[key] == null) continue;
      params[key] = payload[key];
    }
    sendG9(payload.event, params);
    push(payload);
  }

  function emitGa4(eventName, params) {
    sendG9(eventName, params);
    if (eventName === 'search') {
      push({
        event: 'search',
        search_term: params && params.search_term
      });
      return;
    }
    push({ ecommerce: null });
    push({
      event: eventName,
      ecommerce: params
    });
  }

  function facetsByPrefix(tags, prefix) {
    var affinity = window.BDAffinity;
    var facets = affinity ? affinity.facetsFromTags(tags) : [];

    return facets.filter(function (key) {
      return key.indexOf(prefix) === 0;
    });
  }

  function joined(list) {
    return (list || []).join('|');
  }

  function first(list) {
    return list && list.length ? list[0] : undefined;
  }

  /* ---------- 1. taxonomy-aware custom events ------------------------- */

  if (ctx.product) {
    var tags = ctx.product.tags;
    var departments = facetsByPrefix(tags, 'cat-');
    var subcategories = facetsByPrefix(tags, 'subcat-');
    var materials = facetsByPrefix(tags, 'material-');
    var looks = facetsByPrefix(tags, 'look-');

    emitBd({
      event: 'bd_product_view',
      bd_page_type: 'product',
      bd_product_id: ctx.product.id,
      bd_product_handle: ctx.product.handle,
      bd_product_type: ctx.product.type,
      bd_departments: joined(departments),
      bd_subcategories: joined(subcategories),
      bd_materials: joined(materials),
      bd_looks: joined(looks)
    });

    if (ctx.ga4Events) {
      emitGa4('view_item', {
        currency: ctx.currency,
        value: ctx.product.price,
        items: [
          {
            item_id: ctx.product.sku || String(ctx.product.id),
            item_name: ctx.product.title,
            item_brand: ctx.product.vendor,
            item_category: first(departments),
            item_category2: first(subcategories),
            item_category3: first(materials),
            item_category4: first(looks),
            price: ctx.product.price,
            quantity: 1
          }
        ]
      });
    }
  }

  if (ctx.collection) {
    emitBd({
      event: 'bd_category_view',
      bd_page_type: ctx.pageType,
      bd_collection: ctx.collection.handle,
      bd_collection_title: ctx.collection.title,
      bd_departments: joined(facetsByPrefix(ctx.collection.tags, 'cat-'))
    });

    if (ctx.ga4Events) {
      // Count-level only: the page context carries the collection identity,
      // not the rendered product list, so no items array.
      emitGa4('view_item_list', {
        item_list_id: ctx.collection.handle,
        item_list_name: ctx.collection.title
      });
    }
  }

  if (ctx.search && ctx.search.terms) {
    emitBd({
      event: 'bd_search_view',
      bd_page_type: 'search',
      bd_search_term: ctx.search.terms,
      bd_search_results: ctx.search.resultsCount
    });

    if (ctx.ga4Events) {
      emitGa4('search', {
        search_term: ctx.search.terms
      });
    }
  }

  /* ---------- 2. the visitor's standing affinity ---------------------- */

  /**
   * Sent once per page, after bd-affinity.js has folded this page in. This is
   * the event that answers "do customers stay in one category?":
   *   bd_affinity_department  the department they are shopping
   *   bd_affinity_focus       0-1, share of their browsing in that department
   *                           (1 = only ever looked at one department)
   *   bd_affinity_depth       how many signals the score is built from
   * Map department and focus to user-scoped GA4 custom dimensions to segment
   * sessions and conversions by category loyalty.
   */
  function reportAffinity(detail) {
    if (!detail || !detail.department) return;

    emitBd({
      event: 'bd_affinity',
      bd_affinity_department: detail.department,
      bd_affinity_top: joined(
        (detail.top || []).map(function (entry) {
          return entry.key;
        })
      ),
      bd_affinity_focus: Math.round(detail.focus * 100) / 100,
      bd_affinity_depth: detail.depth
    });
  }

  if (window.BDAffinity && window.BDAffinity.ready) {
    reportAffinity({
      department: window.BDAffinity.top('cat-'),
      top: window.BDAffinity.topN(null, 5),
      focus: window.BDAffinity.focus(),
      depth: window.BDAffinity.depth()
    });
  } else {
    document.addEventListener(
      'on:bd-affinity:ready',
      function (event) {
        reportAffinity(event.detail);
      },
      { once: true }
    );
  }

  /* ---------- 3. add to cart / begin checkout ------------------------- */

  /**
   * Every add goes through the theme's cart events (custom.js §2/§3), on all
   * page types — PDP form, collection-card quick add, drawer recommendations:
   *   on:cart:add          variant new to the cart (detail: cart, variantId)
   *   on:line-item:change  quantity of an existing line changed; an add of an
   *                        already-carted variant arrives here instead, with
   *                        oldQuantity < newQuantity
   * Item facts come from the returned cart line (price in cents), so the
   * event is correct even when the added product is not this page's product.
   * The taxonomy category is only known on the added product's own PDP.
   */

  function cartLine(cart, variantId) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (String(items[i].variant_id || items[i].id) === String(variantId)) {
        return items[i];
      }
    }
    return null;
  }

  function pushAddToCart(line, quantity) {
    if (!line || !(quantity > 0)) return;
    var price =
      (line.final_price != null ? line.final_price : line.price) / 100;
    var item = {
      item_id: line.sku || String(line.product_id),
      item_name: line.product_title || line.title,
      item_brand: line.vendor,
      price: price,
      quantity: quantity
    };
    if (ctx.product && String(ctx.product.id) === String(line.product_id)) {
      item.item_category = first(facetsByPrefix(ctx.product.tags, 'cat-'));
    }
    emitGa4('add_to_cart', {
      currency: ctx.currency,
      value: Math.round(price * quantity * 100) / 100,
      items: [item]
    });
  }

  if (ctx.ga4Events) {
    document.addEventListener('on:cart:add', function (event) {
      var detail = event.detail || {};
      var line = cartLine(detail.cart, detail.variantId);
      pushAddToCart(line, line ? line.quantity : 0);
    });

    document.addEventListener('on:line-item:change', function (event) {
      var detail = event.detail || {};
      // Only a quantity increase is an add; without oldQuantity the change
      // direction is unknown (cart-page steppers), so stay silent.
      if (typeof detail.oldQuantity !== 'number') return;
      var added = detail.newQuantity - detail.oldQuantity;
      if (added <= 0) return;
      pushAddToCart(cartLine(detail.cart, detail.variantId), added);
    });

    /* Both checkout entry points (cart drawer and cart page) submit via
       button[name="checkout"]; dynamic checkout buttons (Shop Pay et al.)
       bypass this and stay uncounted, as they were for the app pixel. */
    var lastBeginCheckout = 0;
    document.addEventListener(
      'click',
      function (event) {
        var target = event.target;
        if (!target || !target.closest) return;
        if (!target.closest('button[name="checkout"]')) return;
        var now = Date.now();
        if (now - lastBeginCheckout < 2000) return;
        lastBeginCheckout = now;
        emitGa4('begin_checkout', { currency: ctx.currency });
      },
      true
    );
  }
})();
